import React from "react";
import { get } from "lodash";
import PropTypes from "prop-types";
import { Link } from "@quintype/components";
import { WebEngage, ComScore, DeepBI, Chartbeat, GA, ContentInsights } from "../../../integrations";
import BreadCrumb from "../story/story-templates/shared/breadcrumbs";
import { ZodiacIcon } from "../../atoms/icon";
import Navbar from "../../atoms/secondary-menu";
import { StoryCard } from "../story/story-templates/shared/story-card";
import { DfpAd } from "../../atoms/dfp-ad";
import styles from "./styles.m.css";
import { PAGE_TYPE } from "../../../../isomorphic/constants";

const getZodiacHref = (slug, peyarchiType) => {
  const storySlug = slug.split("/").pop();
  return `/horoscope/${peyarchiType}/${storySlug}`;
};

const getCurrentZodiac = (collection, story, zodiacs) => {
  let currentZodiac = {};
  let index = collection.items.findIndex(({ id }) => story.id === id);
  if (index < 0) {
    return "";
  }
  currentZodiac.type = zodiacs[index].type;
  currentZodiac.name = zodiacs[index]["display_name"];
  return currentZodiac;
};

const SideBar = ({ zodiacs, className = "", collection, currentZodiac, peyarchiType }) => {
  const stories = get(collection, ["items"], []).map(story => story.story);
  const signs = stories.map(({ slug }, index) => {
    const zodiac = zodiacs[index] || {};
    return (
      <Link
        aria-label="Zodiac sign"
        href={getZodiacHref(slug, peyarchiType)}
        key={zodiac.type}
        className={`${styles["sidebar__zodiac"]}`}
      >
        <ZodiacIcon
          type={zodiac.type}
          className={styles["sidebar__zodiac-icon"]}
          hoverDefault={currentZodiac.type === zodiac.type}
        />
        <h3 className={styles["sidebar__zodiac-name"]}>{zodiac.display_name}</h3>
      </Link>
    );
  });
  return <div className={`${className} ${styles["sidebar"]}`}>{signs}</div>;
};

SideBar.propTypes = {
  zodiacs: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      display_name: PropTypes.string,
      dates: PropTypes.string
    })
  ),
  className: PropTypes.string,
  currentZodiac: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string
  }),
  collection: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        story: PropTypes.shape({
          slug: PropTypes.string
        })
      })
    )
  }),
  peyarchiType: PropTypes.string
};

export const GurupeyarchiStoryPage = ({ data, config }) => {
  const story = get(data, ["story"], null);
  if (!story) {
    return null;
  }

  const currentZodiac = getCurrentZodiac(data.collection, data.story, data.zodiacs);
  const name = get(data, ["collection", "name"], "");
  const summary = get(data, ["collection", "summary"], "");
  const subMenu = get(data, ["menuData"], {});
  const peyarchiType = get(data, ["peyarchiType"], "gurupeyarchi");

  React.useEffect(() => {
    WebEngage.registerPageView();
    ComScore.registerPageView();
    DeepBI.loadScript();
    GA.registerPageView(PAGE_TYPE.GURUPEYARCHI_STORY_PAGE, { story, collection: data["collection"] });
    ContentInsights.registerPageView(config.contentInsightsId, story);
    Chartbeat.registerPageView(story);
  }, []);

  return (
    <div className={`${styles["page"]} main-page`}>
      <DfpAd className="hidden-mobile ad-tower-left" adtype="Tower_Left" placement="all" />
      <div>
        {subMenu ? (
          <Navbar
            menuGroup={subMenu}
            slug={"spiritual"}
            className={styles["submenu"]}
            communityColor={subMenu.bgColor}
          />
        ) : null}
        <DfpAd className={`hidden-desktop`} adtype="Mobile_leaderboard" placement="gurupeyarchi-story" />
        <div className={styles["base"]}>
          <div className={`container`}>
            <BreadCrumb config={config} collection={data.collection} story={story} />
            <div className="row">
              <div className={styles.content}>
                <div className={styles["header"]}>
                  <ZodiacIcon type={currentZodiac.type} className={styles["header__zodiac-icon"]} hoverDefault={true} />
                  <div className={styles.header__content}>
                    <h1 className={styles.header__headline}>{`${currentZodiac["name"]} - ${name}`}</h1>
                    <div className={styles.header__summary} dangerouslySetInnerHTML={{ __html: summary }} />
                  </div>
                </div>
                <div className={styles.story}>
                  {story.cards.map((card, index) => (
                    <React.Fragment key={card.id}>
                      <StoryCard card={card} story={story} />
                      {index % 2 === 1 && (
                        <DfpAd className={`hidden-desktop`} adtype="Rectangle" placement="gurupeyarchi-page" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <DfpAd
                className={`${styles["side-ad"]} hidden-mobile`}
                adtype="Full_Page"
                placement="gurupeyarchi-story"
              />
              <SideBar
                className={styles.sidebar}
                zodiacs={data.zodiacs}
                collection={data.collection}
                currentZodiac={currentZodiac}
                peyarchiType={peyarchiType}
              />
              <DfpAd className="hidden-desktop" adtype="Rectangle" placement="gurupeyarchi-story" />
            </div>
          </div>
        </div>
      </div>
      <DfpAd className="hidden-mobile ad-tower-right" adtype="Tower_Right" placement="all" />
    </div>
  );
};

GurupeyarchiStoryPage.propTypes = {
  data: PropTypes.shape({
    collection: PropTypes.shape({
      name: PropTypes.string,
      summary: PropTypes.string,
      items: SideBar.propTypes.collection.items
    }),
    menubarData: PropTypes.shape(Navbar.propTypes),
    zodiacs: SideBar.propTypes.zodiacs,
    peyarchiType: PropTypes.string
  }),
  config: PropTypes.shape({
    contentInsightsId: PropTypes.string
  })
};

export default GurupeyarchiStoryPage;
